// Shared reporting helpers — single source of truth for revenue calculations.
// Must stay byte-for-byte identical to web-pos/src/utils/reportingUtils.ts.
// Any change here MUST be mirrored to keep POS, Admin Reports, Daily Closing,
// and Branches reconciling to the same numbers (Finanzamt / GoBD requirement).

export const BUSINESS_DAY_START_HOUR = 6;

export type DateRangeKey = 'today' | 'yesterday' | '7days' | '30days' | 'week' | 'month' | 'custom';

export interface DateBounds {
  start: Date;
  end: Date | null;
}

export function getBusinessDayStart(now: Date = new Date()): Date {
  const start = new Date(now);
  start.setHours(BUSINESS_DAY_START_HOUR, 0, 0, 0);
  if (now.getHours() < BUSINESS_DAY_START_HOUR) {
    start.setDate(start.getDate() - 1);
  }
  return start;
}

export function getBusinessDayBounds(
  range: DateRangeKey,
  fromStr?: string,
  toStr?: string,
): DateBounds {
  const now = new Date();
  const todayStart = getBusinessDayStart(now);

  switch (range) {
    case 'today':
      return { start: todayStart, end: null };
    case 'yesterday': {
      const yStart = new Date(todayStart);
      yStart.setDate(yStart.getDate() - 1);
      return { start: yStart, end: todayStart };
    }
    case '7days':
    case 'week': {
      const start = new Date(todayStart);
      start.setDate(start.getDate() - 7);
      return { start, end: null };
    }
    case '30days':
    case 'month': {
      const start = new Date(todayStart);
      start.setDate(start.getDate() - 30);
      return { start, end: null };
    }
    case 'custom': {
      const from = fromStr
        ? new Date(`${fromStr}T${String(BUSINESS_DAY_START_HOUR).padStart(2, '0')}:00:00`)
        : todayStart;
      const to = toStr
        ? new Date(`${toStr}T${String(BUSINESS_DAY_START_HOUR).padStart(2, '0')}:00:00`)
        : null;
      const toEnd = to ? new Date(to.getTime() + 24 * 60 * 60 * 1000) : null;
      return { start: from, end: toEnd };
    }
    default:
      return { start: todayStart, end: null };
  }
}

// ---- Order shape helpers -------------------------------------------------

export function parseOrderDate(data: any): Date | null {
  if (!data) return null;
  const created = data.created_at;
  let d: Date | null = null;
  if (created?.toDate) d = created.toDate();
  else if (created?.seconds) d = new Date(created.seconds * 1000);
  else if (typeof created === 'string') d = new Date(created);
  else if (created instanceof Date) d = created;
  return d && !isNaN(d.getTime()) ? d : null;
}

export function parseOrderTotal(data: any): number {
  if (!data) return 0;
  if (typeof data.total === 'number') return data.total;
  if (typeof data.total_amount === 'number') return data.total_amount;
  const parsed = parseFloat(data.total ?? data.total_amount ?? '0');
  return isNaN(parsed) ? 0 : parsed;
}

// ---- Status filter (single source of truth) ------------------------------

// Statuses that represent a paid, sellable, accountable order.
// Anything outside this list is excluded from REVENUE numbers.
// (Active/in-progress orders count toward "active" KPI but NOT revenue
// until they reach a terminal positive state.)
export const REVENUE_TERMINAL_STATUSES = new Set([
  'completed',
  'delivered',
  'picked_up',
  'paid',
]);

export const ACTIVE_STATUSES = new Set([
  'pending',
  'confirmed',
  'preparing',
  'ready',
  'assigned',
  'picking_up',
  'in_transit',
  'assigned_to_driver',
]);

export const NON_REVENUE_STATUSES = new Set([
  'cancelled',
  'canceled',
  'refunded',
  'voided',
  'rejected',
  'failed',
]);

/**
 * Returns true if the order should count toward revenue / VAT totals.
 * Conservative rule:
 *   - excludes anything cancelled / refunded / voided / failed
 *   - excludes orders whose payment is refunded
 *   - excludes orders fully refunded by amount
 *   - includes only terminal positive statuses OR (active + payment completed)
 */
export function isRevenueOrder(o: any): boolean {
  if (!o) return false;
  const status = String(o.status || '').toLowerCase();
  const paymentStatus = String(o.payment_status || '').toLowerCase();

  if (NON_REVENUE_STATUSES.has(status)) return false;
  if (paymentStatus === 'refunded' || paymentStatus === 'cancelled' || paymentStatus === 'voided') {
    return false;
  }

  const total = parseOrderTotal(o);
  const refundAmount = parseFloat(o.refund_amount ?? '0');
  if (!isNaN(refundAmount) && total > 0 && refundAmount >= total) return false;

  if (REVENUE_TERMINAL_STATUSES.has(status)) return true;
  // Pre-paid active orders also count (e.g. online card-paid orders awaiting prep)
  if (paymentStatus === 'completed' || paymentStatus === 'paid') return true;
  return false;
}

/**
 * Counts queued/active orders (prep pipeline). Excludes cancelled/refunded.
 * Intended for the "ACTIVE ORDERS" KPI card.
 */
export function isActiveOrder(o: any): boolean {
  if (!o) return false;
  const status = String(o.status || '').toLowerCase();
  if (NON_REVENUE_STATUSES.has(status)) return false;
  return ACTIVE_STATUSES.has(status);
}

// ---- Item quantity (the "Items Sold" KPI fix) ----------------------------

/**
 * Total UNIT count across an order's line items.
 * Used by the "Items Sold" / "Units Sold" KPI.
 * Returns 0 if items is missing or malformed (so the metric never NaNs).
 */
export function countOrderItems(o: any): number {
  if (!o || !Array.isArray(o.items)) return 0;
  return o.items.reduce((sum: number, item: any) => {
    const qty = parseFloat(item?.quantity ?? item?.qty ?? '1');
    return sum + (isNaN(qty) ? 0 : qty);
  }, 0);
}

export function countOrdersItems(orders: any[]): number {
  return orders.reduce((sum, o) => sum + countOrderItems(o), 0);
}

// ---- VAT split (per German law) ------------------------------------------
//
// Dine-in food: 19% MwSt
// Takeaway / delivery food: 7% MwSt
// Drinks: 19% regardless of channel (alcohol always 19%, soft drinks 19%)
//
// Order-level fallback when item.tax_rate isn't stored.
// This MUST match the Zwischenbericht item-level path for the same order
// or the daily closing report will not reconcile with the live POS report.

export interface OrderVATSplit {
  gross19: number;
  gross7: number;
  net19: number;
  net7: number;
  tax19: number;
  tax7: number;
}

export function splitOrderVAT(o: any): OrderVATSplit {
  const total = parseOrderTotal(o);
  if (!total) return { gross19: 0, gross7: 0, net19: 0, net7: 0, tax19: 0, tax7: 0 };

  // If the order has line items with explicit tax_rate, sum per-item.
  // Item rate is sometimes stored as a percent (7 / 19) and sometimes as a
  // fraction (0.07 / 0.19); accept both.
  if (Array.isArray(o.items) && o.items.length > 0) {
    let g19 = 0, g7 = 0, hasItemRate = false;
    for (const item of o.items) {
      const itemTotal = typeof item.total === 'number'
        ? item.total
        : parseFloat(item.total ?? item.price ?? '0');
      if (isNaN(itemTotal)) continue;
      const rawRate = item.tax_rate ?? item.taxRate;
      if (rawRate !== undefined && rawRate !== null && rawRate !== '') {
        hasItemRate = true;
        const rate = typeof rawRate === 'number' ? rawRate : parseFloat(rawRate);
        if (rate === 19 || rate === 0.19) g19 += itemTotal;
        else g7 += itemTotal;
      }
    }
    if (hasItemRate) {
      const net19 = g19 / 1.19;
      const net7 = g7 / 1.07;
      return {
        gross19: g19,
        gross7: g7,
        net19,
        net7,
        tax19: g19 - net19,
        tax7: g7 - net7,
      };
    }
  }

  // Fallback: use order_type to pick the rate.
  const orderType = String(o.order_type ?? o.orderType ?? o.type ?? '').toLowerCase();
  const isDineIn = orderType === 'dine_in' || orderType === 'dinein' || orderType === 'dine-in';
  if (isDineIn) {
    const net = total / 1.19;
    return { gross19: total, gross7: 0, net19: net, net7: 0, tax19: total - net, tax7: 0 };
  }
  const net = total / 1.07;
  return { gross19: 0, gross7: total, net19: 0, net7: net, tax19: 0, tax7: total - net };
}

export function aggregateVAT(orders: any[]) {
  let gross19 = 0, gross7 = 0, net19 = 0, net7 = 0, tax19 = 0, tax7 = 0;
  for (const o of orders) {
    const s = splitOrderVAT(o);
    gross19 += s.gross19;
    gross7 += s.gross7;
    net19 += s.net19;
    net7 += s.net7;
    tax19 += s.tax19;
    tax7 += s.tax7;
  }
  return {
    grossDineIn: gross19,
    grossTakeaway: gross7,
    netDineIn: net19,
    netTakeaway: net7,
    taxDineIn: tax19,
    taxTakeaway: tax7,
    netRevenue: net19 + net7,
    taxCollected: tax19 + tax7,
    grossRevenue: gross19 + gross7,
  };
}
